/*
 * Copyright (c) 2024, King Living
 * Regional address and validation config per country.
 * Reference: King Living FSD - Field Validation (Atlassian).
 */

import {PHONE_REGION_PATTERNS} from './phone-region-patterns.js';
import {POSTAL_PATTERNS} from './postal-patterns.js';

/**
 * Region-specific address field requirements (per King Living FSD).
 * Company is optional for all regions (field is available to display).
 * State/Territory, State, Province, County, Region all map to one "State" field in CRM per region.
 * @typedef {Object} RegionAddressConfig
 * @property {boolean} stateRequired - Whether state/territory/province/county/region is required
 * @property {string} stateLabel - Label (State/Territory AU, State MY+US, Province CA, County GB, Region NZ)
 * @property {string} postalLabel - Postcode (AU,NZ,MY,GB) | Zip Code (US) | Postal code (SG,CA)
 * @property {string} cityLabel - Suburb (AU) | City/Town (NZ + all others)
 * @property {RegExp} [phonePattern] - Optional phone validation
 * @property {RegExp} [postalPattern] - Postcode / ZIP / postal validation
 * @property {number} [postalMaxLength] - Max length for postal input
 * @property {Array<{value: string, label: string}>} [stateOptions] - State/province options for select
 */

const australianStateOptions = [
  {value: 'ACT', label: 'Australian Capital Territory'},
  {value: 'NSW', label: 'New South Wales'},
  {value: 'NT', label: 'Northern Territory'},
  {value: 'QLD', label: 'Queensland'},
  {value: 'SA', label: 'South Australia'},
  {value: 'TAS', label: 'Tasmania'},
  {value: 'VIC', label: 'Victoria'},
  {value: 'WA', label: 'Western Australia'}
];

const newZealandRegionOptions = [
  {value: 'AUK', label: 'Auckland'},
  {value: 'BOP', label: 'Bay of Plenty'},
  {value: 'CAN', label: 'Canterbury'},
  {value: 'GIS', label: 'Gisborne'},
  {value: 'HKB', label: "Hawke's Bay"},
  {value: 'MBH', label: 'Marlborough'},
  {value: 'MWT', label: 'Manawatu-Whanganui'},
  {value: 'NSN', label: 'Nelson'},
  {value: 'NTL', label: 'Northland'},
  {value: 'OTA', label: 'Otago'},
  {value: 'STL', label: 'Southland'},
  {value: 'TAS', label: 'Tasman'},
  {value: 'TKI', label: 'Taranaki'},
  {value: 'WGN', label: 'Wellington'},
  {value: 'WKO', label: 'Waikato'},
  {value: 'WTC', label: 'West Coast'}
];

const malaysiaStateOptions = [
  {value: 'JHR', label: 'Johor'},
  {value: 'KDH', label: 'Kedah'},
  {value: 'KTN', label: 'Kelantan'},
  {value: 'MLK', label: 'Melaka'},
  {value: 'NSN', label: 'Negeri Sembilan'},
  {value: 'PHG', label: 'Pahang'},
  {value: 'PRK', label: 'Perak'},
  {value: 'PLS', label: 'Perlis'},
  {value: 'PNG', label: 'Pulau Pinang'},
  {value: 'SBH', label: 'Sabah'},
  {value: 'SWK', label: 'Sarawak'},
  {value: 'SGR', label: 'Selangor'},
  {value: 'TRG', label: 'Terengganu'},
  {value: 'KUL', label: 'Kuala Lumpur'},
  {value: 'LBN', label: 'Labuan'},
  {value: 'PJY', label: 'Putrajaya'}
];

const canadaProvinceOptions = [
  {value: 'AB', label: 'Alberta'},
  {value: 'BC', label: 'British Columbia'},
  {value: 'MB', label: 'Manitoba'},
  {value: 'NB', label: 'New Brunswick'},
  {value: 'NL', label: 'Newfoundland and Labrador'},
  {value: 'NS', label: 'Nova Scotia'},
  {value: 'NT', label: 'Northwest Territories'},
  {value: 'NU', label: 'Nunavut'},
  {value: 'ON', label: 'Ontario'},
  {value: 'PE', label: 'Prince Edward Island'},
  {value: 'QC', label: 'Quebec'},
  {value: 'SK', label: 'Saskatchewan'},
  {value: 'YT', label: 'Yukon'}
];

const usStateOptions = [
  {value: 'AL', label: 'Alabama'},
  {value: 'AK', label: 'Alaska'},
  {value: 'AZ', label: 'Arizona'},
  {value: 'AR', label: 'Arkansas'},
  {value: 'CA', label: 'California'},
  {value: 'CO', label: 'Colorado'},
  {value: 'CT', label: 'Connecticut'},
  {value: 'DE', label: 'Delaware'},
  {value: 'DC', label: 'District of Columbia'},
  {value: 'FL', label: 'Florida'},
  {value: 'GA', label: 'Georgia'},
  {value: 'HI', label: 'Hawaii'},
  {value: 'ID', label: 'Idaho'},
  {value: 'IL', label: 'Illinois'},
  {value: 'IN', label: 'Indiana'},
  {value: 'IA', label: 'Iowa'},
  {value: 'KS', label: 'Kansas'},
  {value: 'KY', label: 'Kentucky'},
  {value: 'LA', label: 'Louisiana'},
  {value: 'ME', label: 'Maine'},
  {value: 'MD', label: 'Maryland'},
  {value: 'MA', label: 'Massachusetts'},
  {value: 'MI', label: 'Michigan'},
  {value: 'MN', label: 'Minnesota'},
  {value: 'MS', label: 'Mississippi'},
  {value: 'MO', label: 'Missouri'},
  {value: 'MT', label: 'Montana'},
  {value: 'NE', label: 'Nebraska'},
  {value: 'NV', label: 'Nevada'},
  {value: 'NH', label: 'New Hampshire'},
  {value: 'NJ', label: 'New Jersey'},
  {value: 'NM', label: 'New Mexico'},
  {value: 'NY', label: 'New York'},
  {value: 'NC', label: 'North Carolina'},
  {value: 'ND', label: 'North Dakota'},
  {value: 'OH', label: 'Ohio'},
  {value: 'OK', label: 'Oklahoma'},
  {value: 'OR', label: 'Oregon'},
  {value: 'PA', label: 'Pennsylvania'},
  {value: 'RI', label: 'Rhode Island'},
  {value: 'SC', label: 'South Carolina'},
  {value: 'SD', label: 'South Dakota'},
  {value: 'TN', label: 'Tennessee'},
  {value: 'TX', label: 'Texas'},
  {value: 'UT', label: 'Utah'},
  {value: 'VT', label: 'Vermont'},
  {value: 'VA', label: 'Virginia'},
  {value: 'WA', label: 'Washington'},
  {value: 'WV', label: 'West Virginia'},
  {value: 'WI', label: 'Wisconsin'},
  {value: 'WY', label: 'Wyoming'}
];

const singaporeRegionOptions = [
  {value: 'CENTRAL', label: 'Central'},
  {value: 'EAST', label: 'East'},
  {value: 'NORTH', label: 'North'},
  {value: 'NORTHEAST', label: 'North-East'},
  {value: 'WEST', label: 'West'}
];

// GB counties configured in the Salesforce State/Country picklist (KL-1088).
// `value` is the Salesforce integration code stored in ShippingStateCode/StateCode;
// `label` is the county name. Add a county here once it exists in Salesforce.
const gbCountyOptions = [
  {value: 'BKM', label: 'Buckinghamshire'},
  {value: 'CAM', label: 'Cambridgeshire'},
  {value: 'CMA', label: 'Cumbria'},
  {value: 'DBY', label: 'Derbyshire'},
  {value: 'DEV', label: 'Devon'},
  {value: 'DOR', label: 'Dorset'},
  {value: 'ESS', label: 'Essex'},
  {value: 'ESX', label: 'East Sussex'},
  {value: 'GLS', label: 'Gloucestershire'},
  {value: 'HAM', label: 'Hampshire'},
  {value: 'HRT', label: 'Hertfordshire'},
  {value: 'KEN', label: 'Kent'},
  {value: 'LAN', label: 'Lancashire'},
  {value: 'LEC', label: 'Leicestershire'},
  {value: 'WAR', label: 'Warwickshire'}
];

/** @type {Record<string, RegionAddressConfig>} */
export const REGION_ADDRESS_CONFIG = {
  AU: {
    stateRequired: true,
    stateLabel: 'State/Territory',
    postalLabel: 'Postcode',
    cityLabel: 'Suburb',
    stateOptions: australianStateOptions,
    phonePattern: PHONE_REGION_PATTERNS.AU,
    postalPattern: POSTAL_PATTERNS.AU,
    postalMaxLength: 4,
  },
  NZ: {
    stateRequired: true,
    stateLabel: 'Region',
    postalLabel: 'Postcode',
    cityLabel: 'City/Town',
    stateOptions: newZealandRegionOptions,
    phonePattern: PHONE_REGION_PATTERNS.NZ,
    postalPattern: POSTAL_PATTERNS.NZ,
    postalMaxLength: 4,
  },
  UK: {
    stateRequired: false,
    // County is a dropdown backed by the Salesforce GB State/Country picklist. KL-1088.
    stateLabel: 'County',
    postalLabel: 'Postcode',
    cityLabel: 'City/Town',
    stateOptions: gbCountyOptions,
    phonePattern: PHONE_REGION_PATTERNS.UK,
    postalPattern: POSTAL_PATTERNS.UK,
    postalMaxLength: 8,
  },
  GB: {
    stateRequired: false,
    // County is a dropdown backed by the Salesforce GB State/Country picklist. KL-1088.
    stateLabel: 'County',
    postalLabel: 'Postcode',
    cityLabel: 'City/Town',
    stateOptions: gbCountyOptions,
    phonePattern: PHONE_REGION_PATTERNS.GB,
    postalPattern: POSTAL_PATTERNS.GB,
    postalMaxLength: 8,
  },
  US: {
    stateRequired: true,
    stateLabel: 'State',
    postalLabel: 'ZIP code',
    cityLabel: 'City/Town',
    stateOptions: usStateOptions,
    phonePattern: PHONE_REGION_PATTERNS.US,
    postalPattern: POSTAL_PATTERNS.US,
    postalMaxLength: 10,
  },
  CA: {
    stateRequired: true,
    stateLabel: 'Province',
    postalLabel: 'Postal code',
    cityLabel: 'City/Town',
    stateOptions: canadaProvinceOptions,
    phonePattern: PHONE_REGION_PATTERNS.CA,
    postalPattern: POSTAL_PATTERNS.CA,
    postalMaxLength: 7,
  },
  SG: {
    stateRequired: false,
    stateLabel: '',
    postalLabel: 'Postal code',
    cityLabel: 'City/Town',
    phonePattern: PHONE_REGION_PATTERNS.SG,
    postalPattern: POSTAL_PATTERNS.SG,
    postalMaxLength: 6,
  },
  MY: {
    stateRequired: true,
    stateLabel: 'State',
    postalLabel: 'Postcode',
    cityLabel: 'City/Town',
    stateOptions: malaysiaStateOptions,
    phonePattern: PHONE_REGION_PATTERNS.MY,
    postalPattern: POSTAL_PATTERNS.MY,
    postalMaxLength: 5,
  },
};

/**
 * Get region config by country code (supports both UK and GB for compatibility).
 * @param {string} countryCode - ISO country code (e.g. 'AU', 'UK')
 * @returns {RegionAddressConfig}
 */
export const getRegionAddressConfig = (countryCode) => {
  const code = (countryCode || '').toUpperCase();

  return REGION_ADDRESS_CONFIG[code] || REGION_ADDRESS_CONFIG.AU;
};

export default REGION_ADDRESS_CONFIG;
